import React, { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import LearningObjectProjectFormCst from "../LearningObjectProjectFormCst";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { Association } from "src/interfaces/Common";

interface AddLearningObjectProjectInterface {
  onCancelAction: () => void;
  onSubmitAction: (data: any, list?: any) => void;
}

const AddLearningObjectProject: React.FC<AddLearningObjectProjectInterface> = ({
  onCancelAction,
  onSubmitAction,
}) => {
  const { t } = useTranslation(["projects-learning-object-page"]);
  const { idProject, id } = useParams();
  const { columns, dataDetail } = useFetchDetail(
    `${ServicesURL.getProjectsLearningObject.replace(":budgetProjectId", `${idProject}`)}`,
    `?corporateId=${id}` || "",
    PagesID["projects.learning-object-form"],
  );

  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [coursesAssociation, setCoursesAssociation] = useState<any>([]);
  const [pathAssociation, setPathAssociation] = useState<any>([]);
  const [activityAssociation, setActivityAssociation] = useState<any>([]);
  const [editionAssociation, setEditionAssociation] = useState<any>([]);

  const groupByType = (list: any[]) => {
    return list.reduce(
      (acc, item) => {
        switch (item.type) {
          case "COURSE":
            acc.courses.push(item);
            break;
          case "PATH":
            acc.paths.push(item);
            break;
          case "EDITION":
            acc.editions.push(item);
            break;
          case "ACTIVITY":
            acc.activities.push(item);
            break;
        }
        return acc;
      },
      {
        activities: [] as any[],
        courses: [] as any[],
        paths: [] as any[],
        editions: [] as any[],
      },
    );
  };

  const formatCourses = (courses: any) => {
    const formattedCourses = courses.map((course: any) => ({
      id: course.id,
      label: course.title,
    }));
    setCoursesAssociation(formattedCourses);
  };

  const formatActivities = (activities: any) => {
    const formattedActivities = activities.map((activity: any) => ({
      id: activity.id,
      label: activity.title,
    }));
    setActivityAssociation(formattedActivities);
  };

  const formatPaths = (paths: any) => {
    const formattedPaths = paths.map((path: any) => ({
      id: path.id,
      label: path.title,
    }));
    setPathAssociation(formattedPaths);
  };

  const formatEditions = (editions: any) => {
    const formattedEditions = editions.map((edition: any) => ({
      id: edition.id,
      label: edition.title,
    }));
    setEditionAssociation(formattedEditions);
  };

  const resetLearningObjectAssociations = () => {
    if (dataDetail.output) {
      const tempData = groupByType(dataDetail.output);

      tempData.courses
        ? formatCourses(tempData.courses)
        : setCoursesAssociation([]);
      tempData.paths ? formatPaths(tempData.paths) : setPathAssociation([]);
      tempData.editions
        ? formatEditions(tempData.editions)
        : setEditionAssociation([]);
      tempData.activities
        ? formatActivities(tempData.activities)
        : setActivityAssociation([]);
    }
  };

  const dataDetailFormatted = useMemo(() => {
    if (dataDetail.output) {
      const tempData = groupByType(dataDetail.output);

      if (tempData?.courses) {
        formatCourses(tempData.courses);
      }
      if (tempData?.activities) {
        formatActivities(tempData.activities);
      }
      if (tempData?.paths) {
        formatPaths(tempData.paths);
      }
      if (tempData?.editions) {
        formatEditions(tempData.editions);
      }
      return tempData;
    }
  }, [dataDetail]);

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "activities":
        let urlActivityType =
          "DA_EXTERNAL_RES&type=DA_PHYSICAL_CLASS&type=DA_SCORM&type=DA_VIRTUAL_CLASS&type=DA_XAPI&type=DA_YOUTUBE&type=DA_LINKEDIN&type=DA_URL_RES&type=DA_SURVEY&type=DA_MATERIAL&type=DA_GOALS&type=DA_MEETING&type=DA_FINAL_BALANCE";
        setOpenAssociations(
          value
            ? {
                accessorKey: "activities",
                mediaType: "activities",
                type: "table",
                titleKey: "activities-association",
                pageId: PagesID["corporate.catalog.activities"],
                manageConfigs: {
                  is_standalone: {
                    hasFilter: false,
                  },
                },
                defaultFilters: { type: urlActivityType },
                service: `${ServicesURL.getActivities.replace(
                  ":idCorporate",
                  id || "",
                )}`,
              }
            : value,
        );
        break;
      case "courses":
        setOpenAssociations(
          value
            ? {
                accessorKey: "courses",
                mediaType: "courses",
                type: "table",
                titleKey: "course-association",
                pageId: PagesID["corporate.catalog.courses"],
                manageConfigs: {
                  is_standalone: {
                    hasFilter: false,
                  },
                },
                service: `${ServicesURL.getCoursesAssociation.replace(
                  ":idCorporate",
                  id || "",
                )}`,
              }
            : value,
        );
        break;
      case "paths":
        setOpenAssociations(
          value
            ? {
                accessorKey: "paths",
                mediaType: "paths",
                type: "table",
                titleKey: "path-association",
                pageId: PagesID["corporate.catalog.path"],
                manageConfigs: {
                  is_standalone: {
                    hasFilter: false,
                  },
                },
                service: `${ServicesURL.getPaths.replace(
                  ":idCorporate",
                  id || "",
                )}`,
              }
            : value,
        );
        break;
      case "editions":
        setOpenAssociations(
          value
            ? {
                accessorKey: "editions",
                mediaType: "editions",
                type: "table",
                titleKey: "edition-association",
                pageId: PagesID["corporate.physical-editions"],
                manageConfigs: {
                  is_standalone: {
                    hasFilter: false,
                  },
                },
                service: ServicesURL.getEditions + `?corporateId=${id || ""}`,
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "activities":
        return activityAssociation;
      case "courses":
        return coursesAssociation;
      case "paths":
        return pathAssociation;
      case "editions":
        return editionAssociation;
    }
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "activities":
        setActivityAssociation(
          activityAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "courses":
        setCoursesAssociation(
          coursesAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "paths":
        setPathAssociation(
          pathAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "editions":
        setEditionAssociation(
          editionAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "activities":
        const objToActivityAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return activityAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setActivityAssociation(objToActivityAssociation);
        break;
      case "courses":
        const objToCoursesAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return coursesAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setCoursesAssociation(objToCoursesAssociation);
        break;
      case "paths":
        const objToPathsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return pathAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setPathAssociation(objToPathsAssociation);
        break;
      case "editions":
        const objToEditionsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return editionAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setEditionAssociation(objToEditionsAssociation);
        break;
    }
  };

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerLearningObject } = usePostDetail(
    `${ServicesURL.postProjectsLearningObject.replace(":budgetProjectId", `${idProject}`)}`,
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    const objToSend: any = {
      activities:
        activityAssociation &&
        activityAssociation.map(({ id }: any) => parseInt(id)),
      courses:
        coursesAssociation &&
        coursesAssociation.map(({ id }: any) => parseInt(id)),
      paths:
        pathAssociation && pathAssociation.map(({ id }: any) => parseInt(id)),
      editions:
        editionAssociation &&
        editionAssociation.map(({ id }: any) => parseInt(id)),
    };

    try {
      await triggerLearningObject(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  return (
    <AnimationFadeIn>
      {columns && (
        <Box
          sx={{
            mt: "25px",
            textAlign: "center", // Center the Typography
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: "8px",
            }}
          >
            {t("hero-title")}
          </Typography>

          <LearningObjectProjectFormCst
            row={dataDetailFormatted || []}
            columns={columns}
            onSubmit={onSubmit}
            backButton={() => {
              onCancelAction();
            }}
            setOpenAssociation={(accessorKey, open) =>
              setOpenAssociation(accessorKey, open)
            }
            handleDelete={(accessorKey, el) => handleDelete(accessorKey, el)}
            handleAssociation={(accessorKey, value) =>
              handleAssociation(accessorKey, value)
            }
            getValues={(accessorKey) => getValues(accessorKey)}
            association={openAssociations}
            onReset={() => {
              setActivityAssociation([]);
              setCoursesAssociation([]);
              setPathAssociation([]);
              setEditionAssociation([]);
            }}
            resetLearningObjectAssociations={resetLearningObjectAssociations}
            fullWidth={true}
          />
        </Box>
      )}

      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        callBackConfirmed={() => {
          setOpenToast(undefined);
          onCancelAction();
          onSubmitAction(undefined, undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddLearningObjectProject;
